import { useState, type ReactNode } from 'react'
import type { Job, PayRateType, UpdateJobInput } from '@workflow/contracts'
import { Button, Checkbox, Dialog, Dropdown, Field, IconButton, Icons, TextArea, TextField } from '@workflow/ui'
import { errorSentence } from '@/lib/api/client'
import { useClients, useJobTypes, useUpdateJob } from '@/lib/api/hooks'

interface FormState {
  title: string
  clientId: string
  typeId: string
  priority: string
  description: string
  estimatedHours: string
  playersNeeded: string
  currency: string
  clientRateType: PayRateType | ''
  clientRateAmount: string
  playerRateType: PayRateType | ''
  playerRateAmount: string
  playerRateVisible: boolean
  clientNameVisible: boolean
  notes: string
  internalNotes: string
}

const formFrom = (job: Job): FormState => ({
  title: job.title,
  clientId: job.clientId ?? '',
  typeId: job.typeId,
  priority: job.priority,
  description: job.description ?? '',
  estimatedHours: job.estimatedHours === null ? '' : String(job.estimatedHours),
  playersNeeded: job.playersNeeded === null ? '' : String(job.playersNeeded),
  currency: job.currency ?? '',
  clientRateType: job.clientRateType ?? '',
  clientRateAmount: job.clientRateAmount === null ? '' : String(job.clientRateAmount / 100),
  playerRateType: job.playerRateType ?? '',
  playerRateAmount: job.playerRateAmount === null ? '' : String(job.playerRateAmount / 100),
  playerRateVisible: job.playerRateVisible,
  clientNameVisible: job.clientNameVisible,
  notes: job.notes ?? '',
  internalNotes: job.internalNotes ?? '',
})

/**
 * Only what changed is sent — absent means unchanged, an emptied field sends
 * null. Title and category are never cleared: they are what the job is.
 */
export function changesFrom(initial: Job, form: FormState): UpdateJobInput {
  const input: UpdateJobInput = {}
  const changed = (key: keyof UpdateJobInput, value: string | number | boolean | null) => {
    input[key] = value as never
  }
  const clearable: (keyof UpdateJobInput & keyof FormState)[] = ['clientId', 'description', 'currency', 'notes', 'internalNotes']
  for (const key of clearable) {
    const current = (form[key] as string).trim()
    const before = (initial[key as keyof Job] as string | null) ?? ''
    if (current !== before) changed(key, current === '' ? null : key === 'currency' ? current.toUpperCase() : current)
  }
  for (const key of ['title', 'typeId'] as const) {
    const current = form[key].trim()
    if (current && current !== initial[key]) changed(key, current)
  }
  for (const key of ['playersNeeded', 'estimatedHours'] as const) {
    const value = form[key] === '' ? null : Number(form[key])
    if (value !== initial[key]) changed(key, value)
  }
  // Rates are typed in major units and sent in minor ones; an emptied amount
  // clears the rate it belongs to.
  for (const [rateKey, amountKey] of [
    ['clientRateType', 'clientRateAmount'],
    ['playerRateType', 'playerRateAmount'],
  ] as const) {
    const minor = form[amountKey] === '' ? null : Math.round(Number(form[amountKey]) * 100)
    if (minor !== initial[amountKey]) changed(amountKey, minor)
    if (form[rateKey] === '' && minor === null && initial[rateKey] !== null) changed(rateKey, null)
    else if (form[rateKey] !== '' && form[rateKey] !== initial[rateKey]) changed(rateKey, form[rateKey] as PayRateType)
  }
  if (form.playerRateVisible !== initial.playerRateVisible) changed('playerRateVisible', form.playerRateVisible)
  if (form.clientNameVisible !== initial.clientNameVisible) changed('clientNameVisible', form.clientNameVisible)
  const priority = form.priority as UpdateJobInput['priority']
  if (priority !== initial.priority) changed('priority', priority ?? 'medium')
  return input
}

const RATE_BASIS = [
  { value: '', label: 'Not Set' },
  { value: 'hourly', label: 'Per Hour' },
  { value: 'one_time', label: 'One-Time' },
]

/**
 * Editing a job, in a window over its page — the same calm card the
 * player's task window and the writing windows use. Every staff-owned field
 * of the record, grouped under quiet headings rather than boxes in boxes:
 * the work itself, its words, and its pay. The client's rate is the studio's
 * alone and never reaches a player; the player's rate carries one checkbox
 * for whether players see it. Status is not here: moving a job is a
 * decision, not a field.
 */
export function JobEditDialog({ job, open, onClose }: { job: Job; open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} labelledBy="job-edit-title" className="sm:max-w-2xl">
      {open ? <JobForm initial={job} onSaved={onClose} onCancel={onClose} /> : null}
    </Dialog>
  )
}

export function JobForm({ initial, onSaved, onCancel }: { initial: Job; onSaved?: (job: Job) => void; onCancel?: () => void }) {
  const [form, setForm] = useState<FormState>(() => formFrom(initial))
  const clients = useClients()
  const types = useJobTypes()
  const update = useUpdateJob()
  const set = (key: keyof FormState, value: string) => setForm((current) => ({ ...current, [key]: value }))
  const setFlag = (key: 'playerRateVisible' | 'clientNameVisible', value: boolean) => setForm((current) => ({ ...current, [key]: value }))
  const numberValue = (value: string) => (value === '' || Number.isFinite(Number(value)) ? value : '')
  const wholeValue = (value: string, previous: string) => (value === '' || /^\d+$/.test(value) ? value : previous)
  const changes = changesFrom(initial, form)
  const dirty = Object.keys(changes).length > 0

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!dirty || !form.title.trim()) return
    update.mutate({ id: initial.id, input: changes }, { onSuccess: (job) => onSaved?.(job as Job) })
  }

  return (
    <form
      onSubmit={submit}
      className="flex max-h-[calc(100dvh-2rem)] flex-col rounded-3xl border bg-surface shadow-modal sm:max-h-[calc(100dvh-4rem)]"
    >
      <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-4 sm:px-8">
        <h2 id="job-edit-title" className="wf-heading text-xl text-foreground">Edit Job</h2>
        {onCancel ? <IconButton label="Close" size="sm" onClick={onCancel} tooltipSide="bottom"><Icons.close /></IconButton> : null}
      </div>

      <div className="min-h-0 overflow-y-auto px-6 pb-6 sm:px-8">
        <Group title="The Work">
          <Field label="Title" htmlFor="job-title" className="sm:col-span-2">
            <TextField id="job-title" value={form.title} placeholder="What the studio calls this work" onChange={(event) => set('title', event.target.value)} />
          </Field>
          <Field label="Client" htmlFor="job-client">
            <Dropdown
              id="job-client"
              ariaLabel="Client"
              value={form.clientId}
              options={[
                { value: '', label: 'No Client (Internal)' },
                ...(clients.data ?? []).map((client) => ({ value: client.id, label: client.name })),
              ]}
              onChange={(value) => set('clientId', value)}
            />
          </Field>
          <Field label="Category" htmlFor="job-type">
            <Dropdown
              id="job-type"
              ariaLabel="Category"
              placeholder="Choose a category"
              value={form.typeId}
              options={[
                ...(types.data ?? []).map((type) => ({ value: type.id, label: type.name, description: type.description ?? undefined })),
                // A retired category still answers for the work already under it.
                ...(!(types.data ?? []).some((type) => type.id === initial.typeId)
                  ? [{ value: initial.typeId, label: `${initial.typeName} (Retired)` }]
                  : []),
              ]}
              onChange={(value) => set('typeId', value)}
            />
          </Field>
          <Field label="Priority" htmlFor="job-priority">
            <Dropdown
              id="job-priority"
              ariaLabel="Priority"
              value={form.priority}
              options={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'critical', label: 'Critical' },
              ]}
              onChange={(value) => set('priority', value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Hours" htmlFor="job-hours">
              <TextField id="job-hours" inputMode="decimal" placeholder="e.g. 40" value={form.estimatedHours} onChange={(event) => set('estimatedHours', numberValue(event.target.value))} />
            </Field>
            <Field label="Players" htmlFor="job-players">
              <TextField id="job-players" inputMode="numeric" placeholder="e.g. 5" value={form.playersNeeded} onChange={(event) => set('playersNeeded', wholeValue(event.target.value, form.playersNeeded))} />
            </Field>
          </div>
        </Group>

        <Group title="Words">
          <Field label="Work Description" htmlFor="job-description" className="sm:col-span-2" hint="Shared instructions for every player on this job.">
            <TextArea id="job-description" value={form.description} rows={4} placeholder="What the work is, for whoever picks it up" onChange={(event) => set('description', event.target.value)} />
          </Field>
          <Field label="Client Notes" htmlFor="job-notes" className="sm:col-span-2">
            <TextArea id="job-notes" value={form.notes} rows={2} placeholder="What the client sees" onChange={(event) => set('notes', event.target.value)} />
          </Field>
          <Field label="Internal Notes" htmlFor="job-internal-notes" className="sm:col-span-2">
            <TextArea id="job-internal-notes" value={form.internalNotes} rows={2} placeholder="What stays in the studio" onChange={(event) => set('internalNotes', event.target.value)} />
          </Field>
        </Group>

        <Group title="Pay">
          <Field label="Client Pays" htmlFor="job-client-rate" hint="Only the studio sees this — never players.">
            <RateInput
              id="job-client-rate"
              basis={form.clientRateType}
              amount={form.clientRateAmount}
              onBasis={(value) => set('clientRateType', value)}
              onAmount={(value) => set('clientRateAmount', numberValue(value))}
              label="Client rate basis"
            />
          </Field>
          <Field label="Player Earns" htmlFor="job-player-rate">
            <RateInput
              id="job-player-rate"
              basis={form.playerRateType}
              amount={form.playerRateAmount}
              onBasis={(value) => set('playerRateType', value)}
              onAmount={(value) => set('playerRateAmount', numberValue(value))}
              label="Player rate basis"
            />
            <Checkbox checked={form.playerRateVisible} onChange={(checked) => setFlag('playerRateVisible', checked)} className="mt-1 text-caption">
              Visible To Players
            </Checkbox>
          </Field>
          <Field label="Currency" htmlFor="job-currency">
            <TextField id="job-currency" placeholder="BDT" value={form.currency} onChange={(event) => set('currency', event.target.value.toUpperCase().slice(0, 3))} />
          </Field>
          {form.clientId !== '' ? (
            <Field label="Client Name" htmlFor="job-client-name">
              <Checkbox checked={form.clientNameVisible} onChange={(checked) => setFlag('clientNameVisible', checked)} className="mt-2 text-caption">
                Visible To Players
              </Checkbox>
            </Field>
          ) : null}
        </Group>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-hairline px-6 py-4 sm:justify-end sm:px-8">
        {update.error ? <p className="mr-auto basis-full text-caption text-danger sm:basis-auto">{errorSentence(update.error)}</p> : null}
        {onCancel ? <Button variant="secondary" onClick={onCancel} className="flex-1 sm:flex-none">Cancel</Button> : null}
        <Button type="submit" variant="primary" busy={update.isPending} disabled={!dirty || !form.title.trim()} className="flex-1 sm:flex-none">
          Save Changes
        </Button>
      </div>
    </form>
  )
}

/** One part of the form: a quiet heading, then its fields on a two-column grid — no frame around it. */
function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-6 first:mt-1">
      <p className="mb-3 text-micro text-muted-foreground uppercase">{title}</p>
      <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">{children}</div>
    </section>
  )
}

function RateInput({ id, basis, amount, onBasis, onAmount, label }: {
  id: string
  basis: PayRateType | ''
  amount: string
  onBasis: (value: PayRateType | '') => void
  onAmount: (value: string) => void
  label: string
}) {
  return (
    <div className="flex items-center gap-2">
      <Dropdown ariaLabel={label} className="w-32 shrink-0" value={basis} options={RATE_BASIS} onChange={(value) => onBasis(value as PayRateType | '')} />
      <TextField id={id} inputMode="decimal" placeholder={basis === 'one_time' ? 'Whole Piece' : 'Amount'} value={amount} onChange={(event) => onAmount(event.target.value)} />
    </div>
  )
}
